import { Link } from '@inertiajs/react'
import { Check } from 'lucide-react'
import type { WorkspaceSetup } from '@/types/company'

type StepState = 'done' | 'current' | 'upcoming'

interface SetupStep {
  number: string
  title: string
  description: string
  href: string
  isDone: (setup: WorkspaceSetup) => boolean
}

const SETUP_STEPS: SetupStep[] = [
  {
    number: '01',
    title: 'Company details',
    description: 'Confirm your name and website.',
    href: '/company/profile/edit',
    isDone: (setup) => setup.profileConfirmed,
  },
  {
    number: '02',
    title: 'Add a position',
    description: 'Open your first role.',
    href: '/company/positions',
    isDone: (setup) => setup.positionsCount > 0,
  },
]

const INDICATOR_META: Record<StepState, string> = {
  done: 'bg-primary text-primary-foreground',
  current: 'border-2 border-primary bg-background text-primary',
  upcoming: 'border border-border bg-background text-muted-foreground/70',
}

const TITLE_META: Record<StepState, string> = {
  done: 'text-muted-foreground',
  current: 'font-semibold text-foreground',
  upcoming: 'text-muted-foreground/70',
}

function stepState(step: SetupStep, setup: WorkspaceSetup, currentNumber: string): StepState {
  if (step.isDone(setup)) {
    return 'done'
  }
  return step.number === currentNumber ? 'current' : 'upcoming'
}

function StepTitle({ step, state }: { step: SetupStep; state: StepState }) {
  const titleClass = `font-sans text-[14px] leading-[1.3] ${TITLE_META[state]}`

  if (state === 'upcoming') {
    return <span className={titleClass}>{step.title}</span>
  }

  return (
    <Link href={step.href} className={`${titleClass} underline-offset-4 hover:underline`}>
      {step.title}
    </Link>
  )
}

export function WorkspaceSetupWizard({ setup }: { setup: WorkspaceSetup }) {
  const currentStep = SETUP_STEPS.find((step) => !step.isDone(setup))

  if (!currentStep) {
    return null
  }

  return (
    <aside aria-label="Workspace setup">
      <ol className="flex flex-col gap-5 sm:max-w-[640px] sm:flex-row sm:items-start sm:gap-x-3">
        {SETUP_STEPS.map((step, index) => {
          const state = stepState(step, setup, currentStep.number)
          const notLast = index < SETUP_STEPS.length - 1

          return (
            <li
              key={step.number}
              aria-current={state === 'current' ? 'step' : undefined}
              className={`flex items-start gap-3 ${notLast ? 'sm:flex-1' : ''}`}
            >
              <span
                className={`flex size-7 shrink-0 items-center justify-center rounded-full font-mono text-[10px] font-bold ${INDICATOR_META[state]}`}
              >
                {state === 'done' ? <Check aria-label="Done" className="size-[13px]" /> : step.number}
              </span>
              <div className="flex shrink-0 flex-col gap-[2px] pt-[5px]">
                <StepTitle step={step} state={state} />
                <span className="font-sans text-[12px] leading-[1.45] text-muted-foreground">
                  {step.description}
                </span>
              </div>
              {notLast && (
                <span
                  aria-hidden
                  className={`mt-[13px] hidden h-px flex-1 sm:block ${
                    state === 'done' ? 'bg-primary/40' : 'bg-border'
                  }`}
                />
              )}
            </li>
          )
        })}
      </ol>
    </aside>
  )
}
